import { CheckCircle2, Circle, QrCode, Heart, UserPlus, CalendarCheck } from "lucide-react";

function Frame({ accent, title, children, testid }) {
  return (
    <div className="rounded-xl overflow-hidden shadow-2xl" style={{ background: "var(--bg-deep)", border: `1px solid ${accent}40` }} data-testid={testid}>
      <div className="flex items-center gap-1.5 px-3 py-2" style={{ background: "var(--bg-alt)", borderBottom: "1px solid var(--border-subtle)" }}>
        {["#F87171", "#FBBF24", "#34D399"].map((c) => <span key={c} className="w-2 h-2 rounded-full" style={{ background: c, opacity: 0.7 }} />)}
        <span className="ml-2 text-[0.6rem] uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function TrackerMock({ accent }) {
  const rows = [
    { n: "Sis. Maria Lopez", s: "Home Study · L3", p: 3, t: 7 },
    { n: "Bro. James Carter", s: "Next Steps · L12", p: 12, t: 24 },
    { n: "Sis. Ana Reyes", s: "Needs teacher", p: 1, t: 7 },
  ];
  return (
    <Frame accent={accent} title="thedisciple.tracker" testid="mock-tracker">
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.n} className="flex items-center gap-3 p-2.5 rounded-md" style={{ background: "var(--bg-alt)" }}>
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-[0.6rem] font-semibold shrink-0" style={{ background: `${accent}25`, color: accent }}>{r.n.split(" ")[1][0]}{r.n.split(" ")[2][0]}</div>
            <div className="flex-1 min-w-0">
              <div className="text-xs truncate" style={{ color: "var(--ink)" }}>{r.n}</div>
              <div className="h-1 rounded-full mt-1.5 overflow-hidden" style={{ background: "var(--bg-deep)" }}>
                <div className="h-full rounded-full" style={{ width: `${(r.p / r.t) * 100}%`, background: accent }} />
              </div>
            </div>
            <span className="text-[0.55rem] uppercase tracking-wider shrink-0" style={{ color: r.s === "Needs teacher" ? "#F87171" : "var(--muted)" }}>{r.s}</span>
          </div>
        ))}
      </div>
    </Frame>
  );
}

function AttendanceMock({ accent }) {
  const people = [["Bro. Smith", true], ["Sis. Jones", true], ["Bro. Allen", false], ["Sis. Grant", true]];
  return (
    <Frame accent={accent} title="Sunday AM · North Section" testid="mock-attendance">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs" style={{ color: "var(--ink)" }}>3 of 4 present</span>
        <span className="inline-flex items-center gap-1.5 text-[0.55rem] uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: `${accent}25`, color: accent }}>
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: accent }} /> QR open
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {people.map(([n, ok]) => (
          <div key={n} className="flex items-center gap-2 p-2 rounded-md text-xs" style={{ background: "var(--bg-alt)", color: ok ? "var(--ink)" : "var(--muted)" }}>
            {ok ? <CheckCircle2 size={13} style={{ color: accent }} /> : <Circle size={13} />} {n}
          </div>
        ))}
      </div>
    </Frame>
  );
}

function PrayerMock({ accent }) {
  const list = [["Sis. R.", "6:42 AM", "For my son's surgery"], ["Bro. D.", "7:15 AM", ""], ["Sis. K.", "12:05 PM", "Revival in our city"]];
  return (
    <Frame accent={accent} title="Prayed in the last 24 hours" testid="mock-prayer">
      <div className="space-y-2">
        {list.map(([n, t, note]) => (
          <div key={n} className="p-2.5 rounded-md flex items-start gap-2" style={{ background: "var(--bg-alt)" }}>
            <Heart size={13} style={{ color: accent }} className="mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-xs" style={{ color: "var(--ink)" }}>{n} <span style={{ color: "var(--muted)" }}>· {t}</span></div>
              {note && <div className="text-[0.65rem] italic mt-0.5" style={{ color: "var(--muted)" }}>{note}</div>}
            </div>
          </div>
        ))}
      </div>
    </Frame>
  );
}

function KioskMock({ accent }) {
  const btns = [[UserPlus, "I'm new here"], [CalendarCheck, "Check in"], [Heart, "Prayer"]];
  return (
    <div className="mx-auto max-w-sm rounded-[1.6rem] p-3 shadow-2xl" style={{ background: "#020812", border: `1px solid ${accent}40` }} data-testid="mock-kiosk">
      <div className="rounded-[1.1rem] p-5 text-center" style={{ background: "var(--bg-deep)" }}>
        <div className="text-[0.55rem] uppercase tracking-[0.25em]" style={{ color: accent }}>Welcome to</div>
        <div className="font-serif-display text-xl mt-1" style={{ color: "var(--ink)" }}>Grace Church</div>
        <div className="grid grid-cols-3 gap-2 mt-4">
          {btns.map(([I, l]) => (
            <div key={l} className="rounded-lg py-3 px-1 flex flex-col items-center gap-1.5" style={{ background: `${accent}18`, border: `1px solid ${accent}50` }}>
              <I size={18} style={{ color: accent }} />
              <span className="text-[0.6rem]" style={{ color: "var(--ink)" }}>{l}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-center gap-1.5 text-[0.55rem]" style={{ color: "var(--muted)" }}><QrCode size={10} /> Or scan with your phone</div>
      </div>
    </div>
  );
}

function SocialMock({ accent }) {
  return (
    <Frame accent={accent} title="thedisciple.social" testid="mock-social">
      <div className="p-3 rounded-md" style={{ background: "var(--bg-alt)" }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-[0.6rem]" style={{ background: `${accent}25`, color: accent }}>S</div>
          <div><div className="text-xs" style={{ color: "var(--ink)" }}>Sis. Rachel</div><div className="text-[0.55rem]" style={{ color: "var(--muted)" }}>Grace Church · 2h</div></div>
        </div>
        <p className="text-xs mt-2" style={{ color: "var(--ink-2)" }}>Baptism Sunday! Five new believers went into the water today.</p>
        <div className="mt-2 h-20 rounded-md" style={{ background: `linear-gradient(135deg, ${accent}40, #3B82F640)` }} />
        <div className="mt-2 flex items-center justify-between text-[0.6rem]" style={{ color: "var(--muted)" }}>
          <span>🙌❤️🙏 48</span><span>12 comments</span>
        </div>
      </div>
    </Frame>
  );
}

const MOCKS = { tracker: TrackerMock, attendance: AttendanceMock, prayer: PrayerMock, kiosk: KioskMock, social: SocialMock };

export default function ToolMock({ tool }) {
  const M = MOCKS[tool.slug];
  return <M accent={tool.accent} />;
}
